/**
 * useAppSettings - Shared hook for reading app settings.
 *
 * Fetches from GET /admin/app-settings once, then caches in memory
 * so every component that calls this hook shares the same data
 * without redundant API requests.
 */

import { useState, useEffect } from 'react';
import config from '../config';
import { getStoredToken } from '../services/tokenRefreshService';

export interface AppSettings {
  warnIfFarFromAddress: boolean;
  distanceThresholdKm: number;
  enableBarcodeScanner: boolean;
  enableCompanionApp: boolean;
  enableBarcodeAudit: boolean;
  showRestrictedBadge: boolean;
  /** Global courier: true = Loop (TFS), false = Uber Direct. Per-order overrides win. */
  useLoopDelivery: boolean;
}

// Module-level cache so all consumers share one fetch
let cached: AppSettings | null = null;
let inflight: Promise<AppSettings | null> | null = null;

function fetchSettings(): Promise<AppSettings | null> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const token = getStoredToken();
      if (!token) return null;

      const res = await fetch(`${config.API_URL}/admin/app-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;

      const data = await res.json();
      cached = {
        warnIfFarFromAddress: data.warnIfFarFromAddress ?? true,
        distanceThresholdKm: data.distanceThresholdKm ?? 5.0,
        enableBarcodeScanner: data.enableBarcodeScanner ?? true,
        enableCompanionApp: data.enableCompanionApp ?? false,
        enableBarcodeAudit: data.enableBarcodeAudit ?? false,
        showRestrictedBadge: data.showRestrictedBadge ?? true,
        useLoopDelivery: data.useLoopDelivery ?? false,
      };
      return cached;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSettings | null>(cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) {
      setSettings(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchSettings().then((result) => {
      if (!cancelled) {
        setSettings(result);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, []);

  return { settings, loading };
}

/** Clear the module-level cache so the next useAppSettings consumer re-fetches. */
export function invalidateAppSettings() {
  cached = null;
  inflight = null;
}
